import { JobExecution } from '@lct-jobs/core';

/**
 * A queued execution has no start date yet, so it sorts to the top as the newest thing in the
 * list. Returning a number for every execution also keeps the comparator total: a NaN from one
 * missing date makes the whole sort order-dependent, not just the undated rows.
 */
const startedAt = (execution: JobExecution) => {
  const time = execution.startDate ? new Date(execution.startDate).getTime() : NaN;
  return Number.isNaN(time) ? Infinity : time;
};

/** Newest first, with the id as a tiebreak so equal timestamps keep a stable order. */
export const byStartDateDesc = (a: JobExecution, b: JobExecution) => {
  const aStarted = startedAt(a);
  const bStarted = startedAt(b);
  return aStarted === bStarted ? a.executionId.localeCompare(b.executionId) : bStarted - aStarted;
};

/**
 * Later responses win per execution id, so paging accumulates instead of replacing and a polled
 * refresh updates statuses in place rather than discarding the pages below it.
 *
 * Executions untouched by `incoming` keep their object identity, so a row that did not change can
 * be skipped by a memoised row component.
 */
export const mergeExecutions = (previous: JobExecution[], incoming: JobExecution[]) => {
  const byId = new Map(previous.map((execution) => [execution.executionId, execution]));
  incoming.forEach((execution) => byId.set(execution.executionId, execution));
  return Array.from(byId.values()).sort(byStartDateDesc);
};

export default mergeExecutions;
